import React from "react";
import { useTranslation } from "react-i18next";
import { FullAccessRuleDst, FullAccessRuleSrc, SimpleRulesetRule } from "src/api/generated/frontend_internal_v1";
import clsx from "clsx";
import { TagBadge } from "src/components/badges/tag";
import { GroupBadge } from "src/components/badges/group";
import { DeviceGroupBadge } from "src/components/badges/device-group";
import { SubnetBadge } from "src/components/badges/subnet";
import { Subheading } from "src/components/base/heading";
import { Divider } from "src/components/base/divider";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

/**
 * The properties for {@link ReadOnlyRuleSets}
 */
export type ReadOnlyRuleSetsProps = {
    /** The name of the ruleset */
    name: string;
    /** The description of the ruleset */
    description: string;
    /** The rules that should be displayed */
    rules: Array<SimpleRulesetRule>;
} & (
    | {
          /** The sources the `rules` are applied to */
          sources?: Array<FullAccessRuleSrc>;
          /** Only one of src, dst can be set */
          destinations?: never;
      }
    | {
          /** Only one of src, dst can be set */
          sources?: never;
          /** The destinations the `rules` are applied to */
          destinations?: Array<FullAccessRuleDst>;
      }
);

/**
 * A view for a read only ruleset
 */
export default function ReadOnlyRuleSets(props: ReadOnlyRuleSetsProps) {
    const [t] = useTranslation("component-rulesets");
    const [isTableOpen, setIsTableOpen] = React.useState(false);

    const showDstOrSrc = props.sources !== undefined || props.destinations !== undefined;

    return (
        <div
            className={clsx(
                "min-w-full px-4 py-3 text-left align-middle",
                "flex flex-col gap-4",
                "rounded-lg border border-zinc-950/10 dark:border-zinc-700",
                "bg-zinc-50, dark:bg-zinc-800/50",
                "text-sm/6 font-medium text-zinc-500 dark:text-zinc-400",
            )}
        >
            <div
                className={"grid grid-cols-[1fr_1fr] gap-2 hover:cursor-pointer sm:grid-cols-[1fr_2fr_2fr]"}
                onClick={() => setIsTableOpen(!isTableOpen)}
                role={"button"}
            >
                <div className={"col-span-2 flex items-center gap-2 sm:col-span-1"}>
                    {isTableOpen ? <ChevronDownIcon className={"size-4"} /> : <ChevronRightIcon className={"size-4"} />}
                    <Subheading>{props.name}</Subheading>
                </div>
                {showDstOrSrc && props.destinations && <div>{t("label.source")}</div>}
                {showDstOrSrc && props.sources && <div>{t("label.destination")}</div>}
                <div className={"hidden font-medium sm:block"}>{t("label.description")}</div>
                <div className={"hidden sm:block"} />
                <div className={"flex flex-wrap gap-2"}>
                    {showDstOrSrc &&
                        props.destinations &&
                        props.destinations.map((src, index) => (
                            <div key={index} className="w-fit">
                                {src.type === "Tag" ? (
                                    <TagBadge tag={src.value} />
                                ) : src.type === "Group" ? (
                                    <GroupBadge group={src.value} />
                                ) : src.type === "DeviceGroup" ? (
                                    <DeviceGroupBadge deviceGroup={src.value} />
                                ) : (
                                    <SubnetBadge subnet={src.value} />
                                )}
                            </div>
                        ))}
                    {showDstOrSrc &&
                        props.sources &&
                        props.sources.map((dst, index) => (
                            <div key={index} className="w-fit">
                                {dst.type === "Tag" ? (
                                    <TagBadge tag={dst.value} />
                                ) : dst.type === "Group" ? (
                                    <GroupBadge group={dst.value} />
                                ) : dst.type === "DeviceGroup" ? (
                                    <DeviceGroupBadge deviceGroup={dst.value} />
                                ) : (
                                    <SubnetBadge subnet={dst.value} />
                                )}
                            </div>
                        ))}
                </div>
                <div className={"col-span-2 font-medium sm:col-span-1"}>{props.description}</div>
            </div>
            {isTableOpen && (
                <div
                    className={
                        "flex flex-col gap-4 rounded-lg border border-zinc-950/10 px-2 py-2 dark:border-zinc-700"
                    }
                >
                    <div className={"grid grid-cols-[1fr_1fr] items-center gap-2 sm:grid-cols-[1fr_1fr_2fr]"}>
                        <span>{t("label.protocol")}</span>
                        <span>{t("label.port")}</span>
                        <span className={"hidden sm:block"}>{t("label.description")}</span>

                        <Divider className={"col-span-2 sm:col-span-3"} />

                        {props.rules.map((rule, index) => (
                            <React.Fragment key={index}>
                                <span>{rule.protocol.toUpperCase()}</span>
                                <span>
                                    {["Tcp", "Udp", "Sctp"].includes(rule.protocol)
                                        ? rule.port_range_end
                                            ? `${rule.port} - ${rule.port_range_end}`
                                            : rule.port
                                        : ""}
                                </span>
                                <span className={"hidden text-left break-words whitespace-pre-wrap sm:block"}>
                                    {rule.description}
                                </span>
                                <Divider soft={true} className={"col-span-4 sm:col-span-3"} />
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
